import { Option, Radio, RadioGroup } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { EventAttendee, WinnerEvent } from '../../../models';
import { EventAttendeeValidation } from '../../../models/schemas/questionnaire-wizard-validation.schema';
import { ValidationState } from '../../../shared/validation';
import { CommonLabelAndValueList, ValidationMessageList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput, SelectOptions } from '../../shared-components/form';

interface WinnerDetailsEventAttendeeProps {
  eventAttendee: EventAttendee;
  winnerEvent?: WinnerEvent;
  isEditMode: boolean;
  validation: ValidationState<EventAttendeeValidation>;
}

type EventAttendeeInputText = Pick<EventAttendee, 'firstName' | 'lastName' | 'emailAddress' | 'primaryLanguage' | 'dietaryRequests' | 'accessibilityNeeds' | 'title'>;

interface EventAttendeeInputTextProps {
  label: string;
  id: string;
  required?: boolean;
}

const WinnerDetailsEventAttendeeImpl: React.FC<WinnerDetailsEventAttendeeProps> = (props) => {
  const {
    eventAttendee,
    winnerEvent,
    isEditMode,
    validation,
  } = props;

  const {
    winnersAndFinalistsPageUiStore,
  } = useStores();

  const renderEventAttendeeFormInput = (key: keyof EventAttendeeInputText): React.JSX.Element => {
    const fieldProps: Record<keyof EventAttendeeInputText, EventAttendeeInputTextProps> = {
      firstName: { id: `${eventAttendee.eventAttendeeId}firstName`, label: 'First name *:', required: true },
      lastName: { id: `${eventAttendee.eventAttendeeId}lastName`, label: 'Last name *:', required: true },
      emailAddress: { id: `${eventAttendee.eventAttendeeId}email`, label: 'Email *:', required: true },
      primaryLanguage: { id: `${eventAttendee.eventAttendeeId}primaryLanguage`, label: 'Primary language *:', required: true },
      title: { id: `${eventAttendee.eventAttendeeId}title`, label: 'Title:' },
      dietaryRequests: { id: `${eventAttendee.eventAttendeeId}dietaryRequiests`, label: 'Dietary requests:' },
      accessibilityNeeds: { id: `${eventAttendee.eventAttendeeId}accessibilityNeeds`, label: 'Accessibility needs:' },
    };

    const validationMessages = validation.fieldErrors[key];

    return (
      <CommonLabelAndValueList
        label={fieldProps[key].label}
        labelId={fieldProps[key].id}
        childrenFallback={eventAttendee[key]}
      >
        {
          isEditMode &&
          <FormInput
            required={fieldProps[key]?.required}
            onChange={(value) => winnersAndFinalistsPageUiStore.setEventAttendeeField(
              eventAttendee,
              {
                key,
                value,
              },
            )}
            validationMessage={
              validationMessages?.length
                ? <ValidationMessageList messages={validationMessages} />
                : undefined
            }
            inputProps={{
              'aria-labelledby': fieldProps[key].id,
              maxLength: 100,
              value: eventAttendee[key],
              disabled: !eventAttendee.isAttending,
            }}
          />
        }
      </CommonLabelAndValueList>
    );
  };

  return (
    <FlexBox
      direction='column'
      isFullWidth
      className='winner-finalists-details__summary'
    >
      <FlexBox isFullWidth className='summary-list' >
        <CommonLabelAndValueList
          label='Event:'
          labelId={`${eventAttendee.eventId}event`}
          childrenFallback={winnerEvent?.displayName ?? ''}
        >
          {
            isEditMode &&
            <FormInput
              inputProps={{
                'aria-labelledby': `${eventAttendee.eventId}event`,
                value: winnerEvent?.displayName ?? '',
                readOnly: true,
              }}
            />
          }
        </CommonLabelAndValueList>
        <CommonLabelAndValueList
          label='Attending:'
          labelId={`${eventAttendee.eventId}attending`}
          childrenFallback={eventAttendee.isAttending ? 'Yes' : 'No'}
        >
          {
            isEditMode &&
            <RadioGroup
              aria-labelledby={`${eventAttendee.eventId}attending`}
              layout='horizontal'
              value={eventAttendee.isAttending ? 'yes' : 'no'}
              onChange={(_, data) => winnersAndFinalistsPageUiStore.setEventAttendeeField(
                eventAttendee,
                {
                  key: 'isAttending',
                  value: data.value === 'yes',
                },
              )}
            >
              <Radio value='yes' label='Yes' />
              <Radio value='no' label='No' />
            </RadioGroup>
          }
        </CommonLabelAndValueList>
      </FlexBox>
      <FlexBox isFullWidth className='summary-list' >
        <CommonLabelAndValueList
          label='Prefix:'
          labelId={`${eventAttendee.eventId}prefix`}
          childrenFallback={eventAttendee.prefix}
        >
          {
            isEditMode &&
            <SelectOptions
              onOptionSelect={(data) => winnersAndFinalistsPageUiStore.setEventAttendeeField(
                eventAttendee,
                {
                  key: 'prefix',
                  value: data.optionValue ?? '',
                },
              )}
              validationMessage={
                validation.fieldErrors.prefix?.length
                  ? <ValidationMessageList messages={validation.fieldErrors.prefix} />
                  : undefined
              }
              dropdownProps={{
                'aria-labelledby': `${eventAttendee.eventId}prefix`,
                disabled: !eventAttendee.isAttending,
                value: eventAttendee.prefix,
                inlinePopup: true,
                selectedOptions: [eventAttendee.prefix],
              }}
            >
              <Option value='' text='' />
              <Option value='Mr.'>Mr.</Option>
              <Option value='Mrs.'>Mrs.</Option>
              <Option value='Ms.'>Ms.</Option>
            </SelectOptions>
          }
        </CommonLabelAndValueList>
        {renderEventAttendeeFormInput('firstName')}
      </FlexBox>
      <FlexBox isFullWidth className='summary-list'>
        {renderEventAttendeeFormInput('lastName')}
        {renderEventAttendeeFormInput('title')}
      </FlexBox>
      <FlexBox isFullWidth className='summary-list'>
        {renderEventAttendeeFormInput('emailAddress')}
        {winnerEvent?.eventType === 'Food' && renderEventAttendeeFormInput('primaryLanguage')}
      </FlexBox>
      {
        winnerEvent?.eventType === 'Food' &&
        <FlexBox isFullWidth className='summary-list'>
          {renderEventAttendeeFormInput('dietaryRequests')}
          {renderEventAttendeeFormInput('accessibilityNeeds')}
        </FlexBox>
      }
    </FlexBox>
  );
}

export const WinnerDetailsEventAttendee = observer(WinnerDetailsEventAttendeeImpl);
